import React, {useState} from 'react';
import {useTranslation, withTranslation} from "react-i18next";
import {Modal} from "react-bootstrap";
import MenuProductModalHeader from "../../MenuProductModalHeader";
import ProductModifierGroupSimpleOption from "./Simple/ProductModifierGroupSimpleOption";
import StateItemHelper from "../../../../../../services/Menu/services/State/StateItemHelper";
import {connect} from "react-redux";
import {RootState} from "../../../../../../../reducers";
import {getAvailabilities, getItems, getModifierGroups} from "../../../../../../services/Menu/reducer";
import {getSession} from "../../../../../../services/Session/reducer";
import MenuEditItemViewHelper from "../../../../../../services/Menu/services/ItemView/MenuEditItemViewHelper";
import ProductModifierGroupQuantityInfo from "./ProductModifierGroupQuantityInfo";
import MenuEditItemStateHelper from "../../../../../../services/Menu/services/State/MenuEditItemStateHelper";
import ProductModifierGroupAdvancedOption from "./Advanced/ProductModifierGroupAdvancedOption";
import {scrollToProductRequiredSection} from "../../../../../../utils/product";

interface OwnProps {
    show: boolean;
    onHide: () => void;
    productState: Record<string, any>;
    modifierGroupId: number;
    updateProductState: (productState: Record<string, any>) => void;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ProductModifierGroupsModal = ({
                                        show,
                                        onHide,
                                        productState,
                                        items,
                                        modifierGroups,
                                        session,
                                        availabilities,
                                        modifierGroupId,
                                        updateProductState
                                    }: Props) => {
    const {t} = useTranslation();
    const [internalProductState, setInternalProductState] = useState(productState);
    const productView = MenuEditItemViewHelper.getItemView(items, modifierGroups, availabilities, productState.item_id, undefined, undefined, 1, session.pickup_at, internalProductState, session.order_type);
    const rawModifierGroup = productView.configurable_modifier_groups.find((group: Record<string, any>) => group.id === modifierGroupId);
    const modifierGroup = MenuEditItemViewHelper.parseModifierGroupForIncludedQuantity(rawModifierGroup);

    const options = [...modifierGroup.options]
        .sort((a: Record<string, any>, b: Record<string, any>) => a.position - b.position || a.name.localeCompare(b.name))
        .map(MenuEditItemViewHelper.parseOptionForIncludedQuantity);

    const handleHideModal = () => {
        onHide();
    }

    const changeOptionQuantity = (quantity: number, option: Record<string, any>) => {
        const newProductState = JSON.parse(JSON.stringify(internalProductState));
        StateItemHelper.setQuantityItem(newProductState, modifierGroup, option, quantity, items, modifierGroups, session.order_type);
        setInternalProductState(newProductState);
    }

    const addSubOption = (option: Record<string, any>) => {
        const newProductState = JSON.parse(JSON.stringify(internalProductState));
        StateItemHelper.setItemOption(newProductState, modifierGroup.id, option.id, 1, items, modifierGroups, (option.quantity ?? 0) + 1, session.order_type);
        setInternalProductState(newProductState);
    }

    const handleSave = () => {
        updateProductState(internalProductState);
        handleHideModal();
        setTimeout(() => {
            scrollToProductRequiredSection();
        }, 100)
    };

    const removeOption = (state: Record<string, any>, stateIndex: number) => {
        const newInternalProductState = JSON.parse(JSON.stringify(internalProductState));
        const modifierGroupIndex = newInternalProductState.modifier_groups.findIndex((group: Record<string, any>) => group.modifier_group_id === modifierGroupId);
        const selectedItemIndex = newInternalProductState.modifier_groups[modifierGroupIndex].selected_items.findIndex((item: Record<string, any>) => item.item_id === state.item_id);
        newInternalProductState.modifier_groups[modifierGroupIndex].selected_items.splice(selectedItemIndex + stateIndex, 1);
        setInternalProductState(newInternalProductState);
    }

    const adjustOption = (state: Record<string, any>, stateIndex: number) => {
        const newState = JSON.parse(JSON.stringify(state));
        const newInternalProductState = JSON.parse(JSON.stringify(internalProductState));
        const modifierGroupIndex = newInternalProductState.modifier_groups.findIndex((group: Record<string, any>) => group.modifier_group_id === modifierGroupId);
        const selectedItemIndex = newInternalProductState.modifier_groups[modifierGroupIndex].selected_items.findIndex((item: Record<string, any>) => item.item_id === state.item_id);
        newInternalProductState.modifier_groups[modifierGroupIndex].selected_items[selectedItemIndex + stateIndex] = newState;
        setInternalProductState(newInternalProductState);
    }

    return (
        <Modal show={show} dialogClassName="modal-full product-modal product-modal--small" scrollable
               aria-labelledby="product_modifier_groups_modal" onHide={handleHideModal}>
            <MenuProductModalHeader onClick={handleHideModal} title={modifierGroup.name}
                                    ariaLabelledBy="product_modifier_groups_modal"/>
            <Modal.Body>
                <div className="modal-container">
                    {
                        options.filter(option => !option.only_included_and_cannot_modify).map((option: Record<string, any>) => {
                            const isAdvancedView = MenuEditItemStateHelper.isModifierGroupAdvanced(modifierGroup);

                            if (isAdvancedView) {
                                return (
                                    <div key={`${modifierGroup.id}-${option.id}`} aria-live="polite">
                                        <ProductModifierGroupAdvancedOption modifierGroupOption={option}
                                                                            adjustOption={(optionState: Record<string, any>, stateIndex: number) => adjustOption(optionState, stateIndex)}
                                                                            handleAddSubOption={addSubOption}
                                                                            removeOption={(optionState: Record<string, any>, stateIndex: number) => removeOption(optionState, stateIndex)}
                                                                            modifierGroup={modifierGroup}
                                                                            changeOptionQuantity={changeOptionQuantity}
                                        />
                                    </div>
                                )
                            }

                            return (
                                <div key={`${modifierGroup.id}-${option.id}`} aria-live="polite">
                                    <ProductModifierGroupSimpleOption modifierGroupOption={option}
                                                                      modifierGroup={modifierGroup}
                                                                      changeQuantity={changeOptionQuantity}
                                                                      optionState={MenuEditItemStateHelper.getModifierGroupSelectedItemState(internalProductState, modifierGroup, option)}
                                                                      isProductAvailable
                                    />
                                </div>
                            )
                        })
                    }
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className="d-flex flex-column w-100" style={{gap: "12px"}}>
                    <ProductModifierGroupQuantityInfo quantityInfo={{
                        minPermitted: modifierGroup.adapted_min_permitted,
                        maxPermitted: modifierGroup.adapted_max_permitted,
                        chargeAbove: modifierGroup.adapted_charge_above
                    }} quantity={modifierGroup.adapted_quantity}/>
                    <button className="btn btn-submit btn-primary"
                            disabled={modifierGroup.min_permitted && modifierGroup.quantity < modifierGroup.min_permitted}
                            onClick={handleSave}>{t("common.action.save")}</button>
                </div>
            </Modal.Footer>
        </Modal>
    )
}

const mapStateToProps = (state: RootState) => ({
    items: getItems(state.menu),
    modifierGroups: getModifierGroups(state.menu),
    session: getSession(state.session),
    availabilities: getAvailabilities(state.menu)
});

const connector = connect(mapStateToProps);
export default withTranslation()(connector(ProductModifierGroupsModal));